import { useState, useCallback, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';

export interface LoanListItem {
  loanId: string;
  customerName: string;
  brokerId: string;
  brokerDisplay: string;
  lender: string;
  settlementDate: string;
  loanAmountCents: number;
  loanBalanceCents: number;
  totalUpfrontCents: number;
  totalTrailCents: number;
  lastPaymentMonth: string;
  paymentCount: number;
  status: 'active' | 'discharged';
  dischargeDate: string | null;
  /** Active loan whose lender trail has been $0 for 2+ statement months after paying. */
  trailStopped: boolean;
  /** 'YYYY-MM' of the last month with positive lender trail; null unless trailStopped. */
  lastTrailPaidMonth: string | null;
  /** Calendar months from lastTrailPaidMonth to the loan's latest statement month. */
  trailZeroMonths: number;
  /** Estimate, integer cents: trailZeroMonths x the median of the last three positive months. */
  trailWithheldCents: number;
  /** Lender's comment on the latest $0 month (display only). */
  trailStoppedNote: string | null;
  /** Another loan for the same client still being paid trail (split, offset or refinance hint). */
  trailStoppedSiblingLoanId: string | null;
}

export type LoanListStatusFilter = 'all' | 'active' | 'discharged' | 'trail_stopped';

export interface LoanListFilters {
  search?: string;
  brokerId?: string;  // Legacy - kept for backwards compatibility
  brokerName?: string;  // New: filter by broker display name (handles multiple broker_ids)
  lender?: string;
  settlementDateStart?: string;
  settlementDateEnd?: string;
  loanAmountMin?: number;
  loanAmountMax?: number;
  status?: LoanListStatusFilter;
  entityCode?: string;
}

export interface LoanListSort {
  column: string;
  direction: 'asc' | 'desc';
}

export interface LoanListPagination {
  page: number;
  pageSize: number;
}

interface LoanListRequest {
  companyId: string;
  filters: LoanListFilters;
  pagination: LoanListPagination;
  sort: LoanListSort;
  officeIds?: string[];
  entityCode?: string;
}

interface LoanListResponse {
  loans: LoanListItem[];
  total: number;
  page: number;
  pageSize: number;
  trailStoppedAvailable: boolean;
}

export function useLoanList() {
  const { user, companyId } = useAuth();
  const [loans, setLoans] = useState<LoanListItem[]>([]);
  const [total, setTotal] = useState(0);
  // Server-decided: false for callers who may not see trail (the filter is refused for them).
  const [trailStoppedAvailable, setTrailStoppedAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filters, setFilters] = useState<LoanListFilters>({});

  // Pagination
  const [pagination, setPagination] = useState<LoanListPagination>({
    page: 1,
    pageSize: 50,
  });

  // Sort
  const [sort, setSort] = useState<LoanListSort>({
    column: 'settlement_date',
    direction: 'desc',
  });

  const fetchLoans = useCallback(async () => {
    if (!companyId || !user) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const getLoanListFn = httpsCallable<LoanListRequest, LoanListResponse>(
        functions,
        'getLoanList'
      );

      // Let the backend resolveOfficeScope decide office scoping based on role.
      // Administrators/founders/super_admins see all offices; office_admins/brokers
      // are automatically scoped to their assigned offices server-side.
      // Sending officeIds from the frontend caused a mismatch: the frontend checked
      // only canSeeAllOffices (claim), while the backend also checks role.
      const response = await getLoanListFn({
        companyId,
        filters,
        pagination,
        sort,
      });

      setLoans(response.data.loans);
      setTotal(response.data.total);
      setTrailStoppedAvailable(response.data.trailStoppedAvailable === true);
    } catch (err: any) {
      console.error('Error fetching loan list:', err);
      setError(err.message || 'Failed to fetch loan list');
    } finally {
      setLoading(false);
    }
  }, [companyId, user, filters, pagination, sort]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  // Filter handlers
  const updateFilters = useCallback((newFilters: Partial<LoanListFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to page 1 on filter change
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Pagination handlers
  const setPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setPagination({ page: 1, pageSize });
  }, []);

  // Sort handler
  const updateSort = useCallback((column: string) => {
    setSort((prev) => {
      if (prev.column === column) {
        // Toggle direction
        return {
          column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      } else {
        // New column, default to desc
        return {
          column,
          direction: 'desc',
        };
      }
    });
  }, []);

  const refresh = useCallback(() => {
    fetchLoans();
  }, [fetchLoans]);

  return {
    loans,
    total,
    trailStoppedAvailable,
    loading,
    error,
    filters,
    updateFilters,
    clearFilters,
    pagination,
    setPage,
    setPageSize,
    sort,
    updateSort,
    refresh,
  };
}
